import { useEffect, useState, useMemo, useCallback } from "react";
import { Link, useParams } from "react-router-dom";

import { type StudentDocs } from "../../types/user";
import {
  useUpdateStatus,
  useDirectoryFilenames,
  useFile,
  useDocsStatus,
} from "../../api/documentsApi";
import "./docsCheck.scss";
import Table from "../../components/Table";
import { StudentDocsTableDef } from "../../table/StudentsDocsTableDef";
import { useStaff } from "../../api/staffApi";
import { type DocStatus, type Document } from "../../types/docs";
import { useFilter, MultipleFilter } from "../../components/Filter";
import { getDocsFilterDict } from "../../filters/docsFilter";
import { FileStatusDisplay } from "../../utils/fileStatus";
import { usePagination } from "../../components/Pagination";

const FileViewer = ({
  studentId,
  file,
  userCode,
}: {
  studentId: number;
  file: Document;
  userCode: string;
}) => {
  const { link, filename, status, id } = file;

  const [idx, setIdx] = useState(0);
  const [comment, setComment] = useState("");
  const [fileStatus, setFileStatus] = useState(status);
  const { data: fileResponse, isLoading: isFileLoading } = useFile(link[idx]);

  const { mutateAsync: updateStatus } = useUpdateStatus({
    studentId,
    fileId: id,
  });

  const handleUpdateStatus = useCallback(
    async (status: Exclude<DocStatus, "">) => {
      if (status === "rejected" && !comment) {
        alert("거절 사유를 입력해주세요.");
        return;
      }

      if (status === "approved") {
        setComment("");
      }

      await updateStatus({
        status,
        comment: status === "approved" ? "" : comment,
      });
      setFileStatus(status);
    },
    [comment, updateStatus]
  );

  useEffect(() => {
    setIdx(0);
    setComment(file.comment);
    setFileStatus(file.status);
  }, [file]);

  const { fileDataUrl, contentType } = useMemo(() => {
    if (!fileResponse) return { file: undefined, contentType: undefined };

    const { data, headers } = fileResponse;
    const contentType = headers["content-type"];

    if (contentType?.includes("image")) {
      return {
        fileDataUrl: `data:${contentType};base64,${data}`,
        contentType: contentType,
      };
    }

    if (contentType?.includes("application/pdf")) {
      const byteCharacters = atob(data);
      const byteArrays = [];

      for (let i = 0; i < byteCharacters.length; i++) {
        byteArrays.push(byteCharacters.charCodeAt(i));
      }

      const byteArray = new Uint8Array(byteArrays);
      const url = URL.createObjectURL(
        new Blob([byteArray], { type: contentType })
      );
      return {
        fileDataUrl: url,
        contentType: contentType,
      };
    }

    return {
      fileDataUrl: data,
      contentType: contentType,
    };
  }, [fileResponse]);

  return (
    <div id="fileViewer">
      <p style={{ fontWeight: "700" }}>
        [{userCode}] - {filename} {FileStatusDisplay[fileStatus]}
      </p>
      <div className="filePreviewWrapper">
        <button
          disabled={idx <= 0}
          onClick={() => {
            setIdx((prev) => prev - 1);
          }}
        >
          ◀
        </button>
        <div className="filePreview">
          {isFileLoading ? (
            <p>파일을 로딩 중입니다.</p>
          ) : (
            <>
              {file && contentType?.includes("image") && (
                <img src={fileDataUrl} alt={filename} />
              )}
              {file && contentType?.includes("application/pdf") && (
                <embed
                  src={fileDataUrl}
                  type={contentType}
                  width="595px"
                  height="842px"
                  aria-label={filename}
                ></embed>
              )}
            </>
          )}
        </div>
        <button
          disabled={idx >= link.length - 1}
          onClick={() => {
            setIdx((prev) => prev + 1);
          }}
        >
          ▶
        </button>
      </div>
      <div id="fileReaction">
        <textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
        />
        <button
          className="rejected"
          onClick={() => {
            handleUpdateStatus("rejected");
          }}
        >
          거절
        </button>
        <button
          className="approved"
          onClick={() => {
            handleUpdateStatus("approved");
          }}
        >
          수락
        </button>
      </div>
    </div>
  );
};

const DocsCheck = () => {
  const { directory } = useParams() as {
    directory: string;
  };
  const [fileViewerProps, setFileViewerProps] = useState<{
    studentId: number;
    file: Document;
    userCode: string;
  }>();
  const [filters, setFilters] = useState({});
  const { pagination, setPage } = usePagination();
  const { data: staff } = useStaff();
  const { data: docsNames } = useDirectoryFilenames({
    directory,
    enabled: true,
  });
  const [studentsDocs, setStudentsDocs] = useState<StudentDocs[]>();
  const { data, isLoading } = useDocsStatus({
    directory,
    filters,
    pagination,
  });

  const maxPage = useMemo(() => {
    if (!data) return 0;
    return Math.ceil(data.total / 10);
  }, [data]);

  useEffect(() => {
    if (data) {
      setStudentsDocs(data.students);
    }
  }, [data]);

  const { valueDict, setValue } = useFilter();

  useEffect(() => {
    let value = "";
    if (directory.includes("섭외기관")) value = "individual";
    if (directory.includes("매칭")) value = "mandatory";
    if (directory.includes("근무처")) value = "incumbent";
    setValue("training_type", value);
    setFilters((prev) => ({ ...prev, training_type: value }));
  }, [directory]);

  if (!staff) return <p>로딩 중...</p>;

  return (
    <div className="vertical center fullWidth" id="docsCheckWrapper">
      <Link to="..">
        <button className="moveButton">목록으로</button>
      </Link>
      <h3 style={{ marginTop: "0" }}>{directory}</h3>
      <div className="search">
        <MultipleFilter
          filterDict={getDocsFilterDict(staff.staff.department, directory)}
          valueDict={valueDict}
          setValue={setValue}
          columnCount={4}
        />
        <button
          onClick={() => {
            setPage(0);
            setFilters(valueDict);
          }}
        >
          검색
        </button>
      </div>
      {isLoading && <div>로딩중...</div>}
      {docsNames && studentsDocs && (
        <div className="docsCheck">
          <Table
            columns={StudentDocsTableDef({
              directory,
              filenames: docsNames.filenames,
              onClickFile: ({ studentId, file, userCode }) => {
                setFileViewerProps({
                  studentId,
                  file,
                  userCode,
                });
              },
            })}
            data={studentsDocs || []}
            paginationProps={{ page: pagination.page, setPage, maxPage }}
            columnPinning={{
              left: ["user_code"],
            }}
          />
          {fileViewerProps ? <FileViewer {...fileViewerProps} /> : null}
        </div>
      )}
    </div>
  );
};

export default DocsCheck;
